import { useLanguage } from '../context/LanguageContext';
import { Mail, Phone, MapPin, ArrowUp } from 'lucide-react';

const Footer = () => {
  const { t, language } = useLanguage();
  const currentYear = new Date().getFullYear();

  const scrollTo = (id) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#0F172A] text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">

          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-5">
              <picture>
                <source srcSet="/assets/vegsoft-logo.webp" type="image/webp" />
                <img
                  src="/assets/vegsoft-logo.png"
                  alt="Vegsoft Solutions"
                  width="38"
                  height="38"
                  loading="lazy"
                  decoding="async"
                  className="h-9 w-auto"
                />
              </picture>
              <div className="flex items-baseline">
                <span className="font-heading font-extrabold text-xl text-white tracking-tight">Vegsoft</span>
                <span className="font-heading font-bold text-xl text-[#38BDF8] ml-1 tracking-tight">Solutions</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm max-w-sm mb-6 leading-relaxed font-normal">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="https://wa.me/56947127116"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-500 hover:bg-slate-800 transition-all"
              >
                <Phone className="w-4 h-4" />
              </a>
              <a
                href="mailto:Douglas.vegas@vegsoftsolutions.com"
                className="w-9 h-9 rounded-full border border-slate-700 flex items-center justify-center text-slate-400 hover:text-white hover:border-slate-500 hover:bg-slate-800 transition-all"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Products & Services */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4 text-xs uppercase tracking-wider text-slate-300">
              {language === 'es' ? 'Productos & Soluciones' : 'Products & Solutions'}
            </h3>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="https://catalog.vegsoftsolutions.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Catálogo Hub (SaaS)
                </a>
              </li>
              <li>
                <a
                  href="https://barbershop.vegsoftsolutions.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  Vegsoft Barber (SaaS)
                </a>
              </li>
              <li>
                <a
                  href="https://voycargo.cl/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  VoyCargo (Logística)
                </a>
              </li>
              <li>
                <button
                  onClick={() => scrollTo('services')}
                  className="text-sm text-slate-400 hover:text-white transition-colors text-left"
                >
                  {language === 'es' ? 'Desarrollo de Software & IA' : 'Custom Software & AI'}
                </button>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-heading font-bold text-white mb-4 text-xs uppercase tracking-wider text-slate-300">
              {t.footer.contact}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-[#38BDF8] mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:Douglas.vegas@vegsoftsolutions.com"
                  className="text-sm text-slate-400 hover:text-white transition-colors break-all font-mono text-xs"
                >
                  Douglas.vegas@vegsoftsolutions.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
                <a
                  href="https://wa.me/56947127116"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-slate-400 hover:text-white transition-colors"
                >
                  +56 947 127 116
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#A78BFA] mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-400">Santiago, Chile</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:row items-center justify-between gap-4">
          <p className="text-xs text-slate-500 font-medium">
            © {currentYear} Vegsoft Solutions. {t.footer.rights}
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-800 text-xs text-slate-400 hover:text-white hover:border-slate-700 hover:bg-slate-800 transition-all"
          >
            <ArrowUp className="w-3.5 h-3.5" />
            <span>{language === 'es' ? 'Volver arriba' : 'Back to top'}</span>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
